import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import StatusBadge from '../Components/StatusBadge';
import HelpTooltip from '../Components/HelpTooltip';
import { FaInfoCircle, FaCheckCircle, FaCircle } from 'react-icons/fa';

const ResellerApplication = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    businessName: '',
    businessDescription: '',
    businessType: 'individual',
    contactEmail: '',
    contactPhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    bankDetails: {
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      branchName: ''
    },
    taxInformation: {
      gstNumber: '',
      panNumber: ''
    }
  });

  useEffect(() => {
    checkApplicationStatus();
    // Show welcome modal on first visit
    const hasSeenWelcome = localStorage.getItem('resellerAppWelcomeSeen');
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, []);

  const steps = [
    { number: 1, title: 'Business Info', fields: ['businessName', 'businessDescription', 'businessType'] },
    { number: 2, title: 'Contact Details', fields: ['contactEmail', 'contactPhone'] },
    { number: 3, title: 'Address', fields: ['address.street', 'address.city', 'address.state', 'address.pincode'] },
    { number: 4, title: 'Bank Details', fields: ['bankDetails.accountHolderName', 'bankDetails.accountNumber'] },
    { number: 5, title: 'Tax Info', fields: ['taxInformation.gstNumber', 'taxInformation.panNumber'] }
  ];

  const getCurrentStep = () => {
    // Determine current step based on filled fields
    for (let i = steps.length - 1; i >= 0; i--) {
      const step = steps[i];
      const hasData = step.fields.some(field => {
        const keys = field.split('.');
        let value = formData;
        for (const key of keys) {
          value = value?.[key];
        }
        return value && value.trim() !== '';
      });
      if (hasData) return i + 1;
    }
    return 1;
  };

  useEffect(() => {
    setCurrentStep(getCurrentStep());
  }, [formData]);

  const checkApplicationStatus = async () => {
    try {
      const response = await HttpInterceptor.get('/api/reseller-applications/my-application');
      if (response.data.data) {
        setApplicationStatus(response.data.data);
      }
    } catch (error) {
      // No application found, that's okay
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await HttpInterceptor.post('/api/reseller-applications', formData);
      toast.success('Application submitted successfully! We will review it soon.');
      // Show "What happens next" section
      setTimeout(() => {
        checkApplicationStatus();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        address: { ...formData.address, [field]: value }
      });
    } else if (name.startsWith('bankDetails.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        bankDetails: { ...formData.bankDetails, [field]: value }
      });
    } else if (name.startsWith('taxInformation.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        taxInformation: { ...formData.taxInformation, [field]: value }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  if (applicationStatus) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
        <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <h1 className="text-3xl font-bold mb-4">Application Status</h1>
            <div className="mb-6 flex flex-col items-center gap-4">
              <StatusBadge status={applicationStatus.status} size="lg" />
              {applicationStatus.rejectionReason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md">
                  <p className="text-red-800 font-semibold mb-1">Rejection Reason:</p>
                  <p className="text-red-700">{applicationStatus.rejectionReason}</p>
                </div>
              )}
            </div>
            {applicationStatus.status === 'approved' && (
              <button
                onClick={() => navigate('/reseller/dashboard')}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Go to Reseller Dashboard
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        {/* Welcome Modal */}
        {showWelcome && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-8">
              <h2 className="text-3xl font-bold mb-4 text-purple-600">Welcome to Reseller Application!</h2>
              <div className="space-y-4 mb-6">
                <p className="text-gray-700">
                  Thank you for your interest in becoming a reseller on our marketplace. This application process will take about 5-10 minutes to complete.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">What you'll need:</h3>
                  <ul className="list-disc list-inside text-blue-800 space-y-1">
                    <li>Business information and description</li>
                    <li>Contact details (email and phone)</li>
                    <li>Business address</li>
                    <li>Bank account details</li>
                    <li>Tax information (GST, PAN)</li>
                  </ul>
                </div>
                <p className="text-gray-600 text-sm">
                  <strong>Tip:</strong> Look for the <FaInfoCircle className="inline text-purple-600" /> icon next to fields for helpful guidance.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowWelcome(false);
                  localStorage.setItem('resellerAppWelcomeSeen', 'true');
                }}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700"
              >
                Get Started
              </button>
            </div>
          </div>
        )}

        <h1 className="text-3xl font-bold mb-4">Become a Reseller</h1>
        
        {/* Progress Indicator */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    currentStep >= step.number ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step.number ? (
                      <FaCheckCircle />
                    ) : (
                      <span>{step.number}</span>
                    )}
                  </div>
                  <span className={`text-xs mt-2 ${currentStep >= step.number ? 'text-purple-600 font-semibold' : 'text-gray-500'}`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 ${currentStep > step.number ? 'bg-purple-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Name <span className="text-red-500">*</span>
              <HelpTooltip
                title="Business Name"
                content="Enter the official name of your business as it appears on legal documents. This will be displayed to customers on the marketplace."
              />
            </label>
            <input
              type="text"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Description <span className="text-red-500">*</span>
              <HelpTooltip
                title="Business Description"
                content="Describe your business, the types of products you plan to sell, and your experience. This helps us understand your business better."
              />
            </label>
            <textarea
              name="businessDescription"
              value={formData.businessDescription}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Type <span className="text-red-500">*</span>
            </label>
            <select
              name="businessType"
              value={formData.businessType}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="individual">Individual</option>
              <option value="company">Company</option>
              <option value="partnership">Partnership</option>
              <option value="llp">LLP</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Address</h3>
            <div className="space-y-4">
              <input
                type="text"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
                placeholder="Street Address"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleChange}
                  placeholder="City"
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
                <input
                  type="text"
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleChange}
                  placeholder="State"
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              <input
                type="text"
                name="address.pincode"
                value={formData.address.pincode}
                onChange={handleChange}
                placeholder="Pincode"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Bank Details</h3>
            <div className="space-y-4">
              <input
                type="text"
                name="bankDetails.accountHolderName"
                value={formData.bankDetails.accountHolderName}
                onChange={handleChange}
                placeholder="Account Holder Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
              <input
                type="text"
                name="bankDetails.accountNumber"
                value={formData.bankDetails.accountNumber}
                onChange={handleChange}
                placeholder="Account Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  name="bankDetails.ifscCode"
                  value={formData.bankDetails.ifscCode}
                  onChange={handleChange}
                  placeholder="IFSC Code"
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <input
                  type="text"
                  name="bankDetails.bankName"
                  value={formData.bankDetails.bankName}
                  onChange={handleChange}
                  placeholder="Bank Name"
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Tax Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                name="taxInformation.gstNumber"
                value={formData.taxInformation.gstNumber}
                onChange={handleChange}
                placeholder="GST Number"
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
              <input
                type="text"
                name="taxInformation.panNumber"
                value={formData.taxInformation.panNumber}
                onChange={handleChange}
                placeholder="PAN Number"
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50"
          >
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>

        {/* What Happens Next Section */}
        <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl shadow-md p-6 border border-purple-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <FaInfoCircle className="mr-2 text-purple-600" />
            What Happens Next?
          </h3>
          <div className="space-y-3 text-gray-700">
            <div className="flex items-start">
              <FaCircle className="text-purple-600 text-xs mt-1.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-semibold">1. Review Process</p>
                <p className="text-sm text-gray-600">Our admin team will review your application within 2-3 business days.</p>
              </div>
            </div>
            <div className="flex items-start">
              <FaCircle className="text-purple-600 text-xs mt-1.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-semibold">2. Notification</p>
                <p className="text-sm text-gray-600">You'll receive an email notification once your application is reviewed.</p>
              </div>
            </div>
            <div className="flex items-start">
              <FaCircle className="text-purple-600 text-xs mt-1.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-semibold">3. Get Started</p>
                <p className="text-sm text-gray-600">Once approved, you can access your reseller dashboard and start adding products!</p>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-purple-200">
            <p className="text-sm text-gray-600">
              <strong>Need help?</strong> Visit our <a href="/help" className="text-purple-600 hover:underline">Help Center</a> or contact support.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResellerApplication;

